import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class WorkflowFormControlService {
    /**
     * Finds a GCK control in the form hierarchy.
     * First searches in the same step (parent FormGroup), then recursively searches the entire form.
     */
    public findGckControl(control: FormControl): FormControl | null {
        const gckInSameStep = control.parent?.get('gck') as FormControl | null;
        if (gckInSameStep) return gckInSameStep;

        const rootForm = control.root;
        return this.findControlRecursively(rootForm, 'gck-participants');
    }

    /**
     * Recursively searches for a control by name in the form tree.
     */
    public findControlRecursively(control: AbstractControl, controlName: string): FormControl | null {
        if (!control) return null;

        if (control.get && typeof control.get === 'function') {
            const directChild = control.get(controlName);
            if (directChild instanceof FormControl) {
                return directChild;
            }
        }

        if (control instanceof FormGroup) {
            const controls = control.controls;
            const entries = Object.entries(controls);

            for (const [_, childControl] of entries) {
                const found = this.findControlRecursively(childControl, controlName);
                if (found) return found;
            }
        }

        return null;
    }

    /**
     * Generic method to find any control by name in the form hierarchy.
     * Searches parent first, then falls back to recursive root search.
     */
    public findControl(sourceControl: FormControl, controlName: string, fallbackName?: string): FormControl | null {
        const inParent = sourceControl.parent?.get(controlName) as FormControl | null;
        if (inParent) return inParent;

        const searchName = fallbackName || controlName;
        const rootForm = sourceControl.root;
        return this.findControlRecursively(rootForm, searchName);
    }
}
